/**
 * Safe message-text tokenizer + renderer.
 *
 * Turns a chat message into tokens — plain text, @mentions (staff and
 * members), #channel links, @everyone-style broadcasts, #hashtags, and links (bare http(s) URLs, bare www. domains and
 * markdown `[label](url)` links, which the Media Library inserts as
 * `📎 [name](short_url)`). Rendering builds React elements only; there is no
 * dangerouslySetInnerHTML, and a link is emitted only when its href parses as
 * http: or https: — `javascript:`, `data:` and friends stay plain text.
 */

import React, { createContext, useContext } from 'react';
import { cn } from '@/lib/utils';
import TranslationContext from '@/contexts/TranslationContext';

export type MessageToken =
  | { type: 'text'; value: string }
  | { type: 'mention'; name: string; kind: string; id: string; raw: string }
  | { type: 'channel'; name: string; id: string; raw: string }
  | { type: 'broadcast'; value: string }
  | { type: 'hashtag'; tag: string; value: string }
  | { type: 'link'; href: string; label: string; markdown: boolean };

// One alternation, tried left-to-right at each position:
//   1. mention        @[Name](user:12)  |  @[Name](member:34)
//   2. markdown link  [label](url)        (url may contain one balanced (...) pair)
//   3. bare URL       https://…  |  www.…
//   4. broadcast      @everyone / @here / @channel
//   5. hashtag        #word
//   6. channel link   #[general](channel:5)
const TOKEN_RE =
  /@\[([^\]\n]+)\]\((\w+):(\d+)\)|\[([^\]\n]+)\]\(([^()\s]+(?:\([^()\s]*\)[^()\s]*)?)\)|((?:https?:\/\/|www\.)[^\s<>"]+)|@(everyone|here|channel)\b|#(\w+)|#\[([^\]\n]+)\]\(channel:(\d+)\)/gi;

const TRAILING_PUNCT = new Set(['.', ',', ';', ':', '!', '?', "'", '"', '*', '_', '>']);

function count(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n += 1;
  return n;
}

/** Drop sentence punctuation and unbalanced closing brackets from a URL's tail. */
export function trimUrlTail(url: string): string {
  let out = url;
  for (;;) {
    const last = out.charAt(out.length - 1);
    if (TRAILING_PUNCT.has(last)) {
      out = out.slice(0, -1);
    } else if (last === ')' && count(out, ')') > count(out, '(')) {
      out = out.slice(0, -1);
    } else if (last === ']' && count(out, ']') > count(out, '[')) {
      out = out.slice(0, -1);
    } else {
      return out;
    }
  }
}

/**
 * Normalise a candidate link target to an absolute http(s) URL, or null when it
 * is anything else (javascript:, data:, mailto:, relative paths, garbage).
 */
export function toSafeHref(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const candidate = raw.trim();
  if (!candidate) return null;
  const withScheme = /^www\./i.test(candidate) ? `https://${candidate}` : candidate;
  if (!/^https?:\/\//i.test(withScheme)) return null;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

function pushText(tokens: MessageToken[], value: string) {
  if (!value) return;
  const prev = tokens[tokens.length - 1];
  if (prev && prev.type === 'text') prev.value += value;
  else tokens.push({ type: 'text', value });
}

export function tokenizeMessage(content: string | null | undefined): MessageToken[] {
  const tokens: MessageToken[] = [];
  if (!content) return tokens;

  const re = new RegExp(TOKEN_RE.source, TOKEN_RE.flags);
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(content)) !== null) {
    const start = match.index;
    const whole = match[0];
    const prevChar = start > 0 ? content.charAt(start - 1) : '';

    if (match[1] !== undefined) {
      pushText(tokens, content.slice(cursor, start));
      tokens.push({ type: 'mention', name: match[1], kind: match[2], id: match[3], raw: whole });
      cursor = start + whole.length;
      continue;
    }

    if (match[4] !== undefined) {
      const href = toSafeHref(match[5]);
      pushText(tokens, content.slice(cursor, start));
      if (href) tokens.push({ type: 'link', href, label: match[4], markdown: true });
      else pushText(tokens, whole); // unsafe target: show the source text, never a link
      cursor = start + whole.length;
      continue;
    }

    if (match[6] !== undefined) {
      // A bare www. glued to a word / path / email ("foo.www.x", "a@www.x") is
      // not a link start.
      if (/^www\./i.test(match[6]) && /[\w.@/-]/.test(prevChar)) {
        re.lastIndex = start + 4;
        continue;
      }
      const url = trimUrlTail(match[6]);
      const href = toSafeHref(url);
      if (!href) {
        re.lastIndex = start + Math.max(url.length, 1);
        continue;
      }
      pushText(tokens, content.slice(cursor, start));
      tokens.push({ type: 'link', href, label: url, markdown: false });
      cursor = start + url.length;
      re.lastIndex = cursor;
      continue;
    }

    if (match[7] !== undefined) {
      if (/\w/.test(prevChar)) continue; // "me@here" is not a broadcast
      pushText(tokens, content.slice(cursor, start));
      tokens.push({ type: 'broadcast', value: whole });
      cursor = start + whole.length;
      continue;
    }

    if (match[9] !== undefined) {
      pushText(tokens, content.slice(cursor, start));
      tokens.push({ type: 'channel', name: match[9], id: match[10], raw: whole });
      cursor = start + whole.length;
      continue;
    }

    if (match[8] !== undefined) {
      if (/[\w&/]/.test(prevChar)) continue; // "a#b", "&#39;", "/path#frag"
      pushText(tokens, content.slice(cursor, start));
      tokens.push({ type: 'hashtag', tag: match[8], value: whole });
      cursor = start + whole.length;
      continue;
    }
  }

  pushText(tokens, content.slice(cursor));
  return tokens;
}

/** Unique link hrefs in a message, in order of appearance (max `limit`). */
export function extractLinkUrls(content: string | null | undefined, limit = 3): string[] {
  const seen: string[] = [];
  for (const token of tokenizeMessage(content)) {
    if (token.type !== 'link') continue;
    if (!seen.includes(token.href)) seen.push(token.href);
    if (seen.length >= limit) break;
  }
  return seen;
}

/** First link href in a string, or null. */
export function firstLinkUrl(content: string | null | undefined): string | null {
  return extractLinkUrls(content, 1)[0] ?? null;
}

/**
 * Where a clicked member mention / channel link goes. The default opens a
 * member's profile through the app-wide `open-member` event (the same one
 * search results, households and reports use); channel links only act when a
 * messaging workspace provides `openChannel`.
 */
export interface MessageNavigation {
  openMember?: (memberId: number, name: string) => void;
  openChannel?: (channelId: number, name: string) => void;
}

export function openMemberProfile(memberId: number) {
  window.dispatchEvent(new CustomEvent('open-member', { detail: { memberId } }));
}

const MessageNavigationContext = createContext<MessageNavigation>({ openMember: openMemberProfile });

export function MessageNavigationProvider({
  value,
  children,
}: {
  value: MessageNavigation;
  children: React.ReactNode;
}) {
  const parent = useContext(MessageNavigationContext);
  return (
    <MessageNavigationContext.Provider value={{ ...parent, ...value }}>{children}</MessageNavigationContext.Provider>
  );
}

/** Parse a numeric id from markup; anything but a positive safe integer is rejected. */
function numericId(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

interface MessageTextProps {
  content: string | null | undefined;
  /** "inverted" = rendered on a dark/blue bubble, so links stay legible. */
  tone?: 'default' | 'inverted';
  className?: string;
}

/**
 * The composer sends the literal "[Attachment]" when a file goes out with no
 * text (the API needs a body). It's a placeholder, not something the sender
 * wrote, so a message that carries its attachment shows no text line.
 */
export const ATTACHMENT_PLACEHOLDER = '[Attachment]';
export function visibleMessageText(content: string | null | undefined, hasAttachments: boolean): string {
  const text = content ?? '';
  return hasAttachments && text.trim() === ATTACHMENT_PLACEHOLDER ? '' : text;
}

/**
 * One-line plain text for a list preview: `@[Name](user:12)` → `@Name`,
 * `[label](url)` → `label`, `#[general](channel:5)` → `#general`. The server
 * cuts previews at 100 characters, so markup cut off mid-way is dropped.
 */
export function messagePreviewText(content: string | null | undefined): string {
  const cut = (content ?? '').replace(/@?#?\[[^\]\n]*(\]\([^)\s]*)?$/, '');
  return tokenizeMessage(cut)
    .map((tok) => {
      switch (tok.type) {
        case 'mention':
          return `@${tok.name}`;
        case 'channel':
          return `#${tok.name}`;
        case 'link':
          return tok.label;
        default:
          return tok.value;
      }
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Message body with mentions, hashtags and safe links rendered inline. */
export function MessageText({ content, tone = 'default', className }: MessageTextProps) {
  // Read the translation context directly: MessageText also renders outside
  // the app's TranslationProvider (pop-outs, tests), where it falls back to English.
  const i18n = useContext(TranslationContext);
  const t = (key: string, fallback: string) => (i18n ? i18n.t(key, fallback) : fallback);
  const nav = useContext(MessageNavigationContext);
  const tokens = tokenizeMessage(content);
  const chipButton =
    'inline cursor-pointer rounded px-1 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60';
  const linkClass =
    tone === 'inverted'
      ? 'underline decoration-white/60 underline-offset-2 hover:decoration-white text-white break-all'
      : 'text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:decoration-blue-600 dark:text-blue-400 break-all';

  return (
    <span className={className}>
      {tokens.map((token, index) => {
        switch (token.type) {
          case 'mention': {
            const memberId = token.kind.toLowerCase() === 'member' ? numericId(token.id) : null;
            if (memberId != null) {
              const memberClass =
                tone === 'inverted'
                  ? 'bg-white/25 text-white hover:bg-white/35'
                  : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-200';
              return nav.openMember ? (
                <button
                  key={index}
                  type="button"
                  className={cn(chipButton, memberClass)}
                  data-testid="member-mention"
                  data-member-id={memberId}
                  title={t('messaging.mentions.openMember', 'Open member profile')}
                  onClick={(e) => {
                    e.stopPropagation();
                    nav.openMember?.(memberId, token.name);
                  }}
                >
                  @{token.name}
                </button>
              ) : (
                <span key={index} className={cn('rounded px-1', memberClass)} data-testid="member-mention">
                  @{token.name}
                </span>
              );
            }
            return (
              <span key={index} className="bg-primary/20 text-primary rounded px-1">
                @{token.name}
              </span>
            );
          }
          case 'channel': {
            const channelId = numericId(token.id);
            if (channelId == null) return <React.Fragment key={index}>{token.raw}</React.Fragment>;
            const channelClass =
              tone === 'inverted'
                ? 'text-white underline decoration-white/60 underline-offset-2 hover:decoration-white'
                : 'text-blue-600 hover:underline dark:text-blue-400';
            return nav.openChannel ? (
              <button
                key={index}
                type="button"
                className={cn(chipButton, 'px-0', channelClass)}
                data-testid="channel-link"
                data-channel-id={channelId}
                title={t('messaging.mentions.openChannel', 'Open channel')}
                onClick={(e) => {
                  e.stopPropagation();
                  nav.openChannel?.(channelId, token.name);
                }}
              >
                #{token.name}
              </button>
            ) : (
              <span key={index} className={cn('font-medium', channelClass)} data-testid="channel-link">
                #{token.name}
              </span>
            );
          }
          case 'broadcast':
            return (
              <span key={index} className="bg-yellow-200 dark:bg-yellow-800 rounded px-1">
                {token.value}
              </span>
            );
          case 'hashtag':
            return (
              <span key={index} className="text-primary cursor-pointer hover:underline">
                {token.value}
              </span>
            );
          case 'link':
            return (
              <a
                key={index}
                href={token.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={cn(linkClass)}
                onClick={(e) => e.stopPropagation()}
              >
                {token.label}
              </a>
            );
          default:
            return <React.Fragment key={index}>{token.value}</React.Fragment>;
        }
      })}
    </span>
  );
}

export default MessageText;
